/**
 * CLI approval page (/cli?code=XXXX-XXXX).
 *
 * `ikompete login` prints a link here. The signed-in user checks the code and the client, then
 * approves or denies. Not linked from navigation; reached only from the CLI's link.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../components/common/Button';
import { useAuth } from '../hooks/useAuth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { apiService } from '../services/api';
import { errorMessage } from '../services/manageApi';
import { CliLoginRequest } from '../types/interfaces';
import { formatDateTime } from '../utils/dates';
import './CliApprove.css';

export const CLI_APPROVED = 'CLI approved. You can return to your terminal.';
export const CLI_DENIED = 'Request denied. Nothing was shared with the CLI.';
export const CLI_INVALID = 'This code is invalid or has expired. Run ikompete login again.';

type Phase = 'enter' | 'loading' | 'ready' | 'sending' | 'approved' | 'denied' | 'invalid';

const statusOf = (err: unknown) => (err as { status?: unknown } | null)?.status;
const isInvalid = (err: unknown) => statusOf(err) === 404 || statusOf(err) === 409;

const CliApprove: React.FC = () => {
  useDocumentTitle('Approve CLI login');
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const initialCode = searchParams.get('code') ?? '';

  const [code, setCode] = useState(initialCode);
  const [phase, setPhase] = useState<Phase>(initialCode.trim() ? 'loading' : 'enter');
  const [request, setRequest] = useState<CliLoginRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Answers for a code the user has since edited are ignored.
  const lookupId = useRef(0);

  const lookUp = async (value: string) => {
    const id = ++lookupId.current;
    setPhase('loading');
    setError(null);
    try {
      const res = await apiService.get('/auth/cli/request', { code: value });
      if (id !== lookupId.current) return;
      const data = res.data as CliLoginRequest;
      if (data.status !== 'pending') {
        setPhase('invalid');
        return;
      }
      setRequest(data);
      setPhase('ready');
    } catch (err) {
      if (id !== lookupId.current) return;
      if (isInvalid(err)) {
        setPhase('invalid');
      } else {
        setError(errorMessage(err, 'Could not check this code. Try again.'));
        setPhase('enter');
      }
    }
  };

  useEffect(() => {
    if (initialCode.trim()) lookUp(initialCode);
    // Look up the code from the link once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCodeChange = (value: string) => {
    lookupId.current += 1;
    setCode(value);
    setRequest(null);
    setError(null);
    setPhase('enter');
  };

  const onContinue = (event: React.FormEvent) => {
    event.preventDefault();
    if (code.trim()) lookUp(code);
  };

  const answer = async (action: 'approve' | 'deny') => {
    setPhase('sending');
    setError(null);
    try {
      await apiService.post(`/auth/cli/${action}`, { user_code: code });
      setPhase(action === 'approve' ? 'approved' : 'denied');
    } catch (err) {
      if (isInvalid(err)) {
        setPhase('invalid');
      } else {
        setError(errorMessage(err, 'Something went wrong. Try again.'));
        setPhase('ready');
      }
    }
  };

  const done = phase === 'approved' || phase === 'denied' || phase === 'invalid';
  const resultTone = phase === 'approved' ? 'success' : phase === 'denied' ? 'info' : 'error';
  const resultText = phase === 'approved' ? CLI_APPROVED : phase === 'denied' ? CLI_DENIED : CLI_INVALID;
  const showDetails = (phase === 'ready' || phase === 'sending') && request;

  return (
    <div className="cli-approve">
      <div className="cli-approve__card">
        <h1 className="cli-approve__title">Sign in to the iKompete CLI</h1>

        {done ? (
          <p className={`cli-approve__result cli-approve__result--${resultTone}`} role="status">
            {resultText}
          </p>
        ) : (
          <>
            <p className="cli-approve__lead">
              Check that this code matches the one in your terminal.
            </p>

            <form className="cli-approve__code-form" onSubmit={onContinue}>
              <label className="cli-approve__label" htmlFor="cli-approve-code">Code</label>
              <div className="cli-approve__code-row">
                <input
                  id="cli-approve-code"
                  className="cli-approve__code-input"
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  placeholder="XXXX-XXXX"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  disabled={phase === 'loading' || phase === 'sending'}
                />
                {!showDetails && (
                  <Button
                    type="submit"
                    variant="primary"
                    loading={phase === 'loading'}
                    disabled={!code.trim()}
                  >
                    Continue
                  </Button>
                )}
              </div>
            </form>

            {showDetails && (
              <>
                <dl className="cli-approve__details">
                  <div className="cli-approve__detail">
                    <dt>Signed in as</dt>
                    <dd>{user?.username}</dd>
                  </div>
                  <div className="cli-approve__detail">
                    <dt>Requested by (as reported by the program)</dt>
                    <dd>{request.client_name || 'Unknown client'}</dd>
                  </div>
                  <div className="cli-approve__detail">
                    <dt>Expires</dt>
                    <dd>{formatDateTime(request.expires_at)}</dd>
                  </div>
                </dl>

                <div className="cli-approve__warning">
                  <p>
                    <strong>This lets the iKompete CLI act as you.</strong> It can see and do
                    what your account can, until you sign out of the CLI.
                  </p>
                  <p>
                    If you didn't just run <code>ikompete login</code>, choose Deny.
                  </p>
                </div>
              </>
            )}

            {error && <p className="cli-approve__error" role="alert">{error}</p>}

            {showDetails && (
              <div className="cli-approve__actions">
                <Button
                  variant="outline"
                  onClick={() => answer('deny')}
                  disabled={phase === 'sending'}
                >
                  Deny
                </Button>
                <Button
                  variant="primary"
                  onClick={() => answer('approve')}
                  disabled={phase === 'sending'}
                >
                  Approve
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CliApprove;
